import { ZERO_RADIUS } from "./create-shared";
import { findGroup, locateRow } from "./find";
import type {
  BorderStyle,
  Column,
  CornerRadius,
  DocumentSection,
  Row,
} from "./types";

function hasNonZeroRadius(radius: CornerRadius | undefined): boolean {
  const value = radius ?? ZERO_RADIUS;
  return (
    value.topLeft > 0 ||
    value.topRight > 0 ||
    value.bottomRight > 0 ||
    value.bottomLeft > 0
  );
}

function hasVisibleBorder(border: BorderStyle): boolean {
  if (border.style === 'none') {
    return false;
  }
  const { width } = border;
  return width.top > 0 || width.right > 0 || width.bottom > 0 || width.left > 0;
}

function columnHasSurfaceStyles(column: Column): boolean {
  if (
    column.backgroundColor !== 'transparent' &&
    column.backgroundColor !== ''
  ) {
    return true;
  }
  if (column.backgroundImage) {
    return true;
  }
  if (hasVisibleBorder(column.border)) {
    return true;
  }
  return hasNonZeroRadius(column.borderRadius);
}

export function isRowEmpty(row: Row): boolean {
  return row.columns.every(column => column.blocks.length === 0);
}

export function rowHasSurfaceStyles(row: Row): boolean {
  if (row.backgroundColor !== 'transparent' && row.backgroundColor !== '') {
    return true;
  }
  if (row.backgroundImage) {
    return true;
  }
  if (hasVisibleBorder(row.border)) {
    return true;
  }
  if (hasNonZeroRadius(row.borderRadius)) {
    return true;
  }
  return row.columns.some(columnHasSurfaceStyles);
}

export function shouldRenderRow(row: Row, editorMode: boolean): boolean {
  return editorMode || !isRowEmpty(row) || rowHasSurfaceStyles(row);
}

export function applySpacerRowDefaults(row: Row): void {
  row.padding = { ...row.padding, top: 0, bottom: 0 };
  row.contentGutter = false;
  for (const column of row.columns) {
    column.padding = { ...column.padding, left: 0, right: 0 };
  }
}

export function getSectionSlotInsertBounds(sections: DocumentSection[]): {
  min: number;
  max: number;
} {
  return { min: 0, max: sections.length };
}

export function clampSectionInsertIndex(
  sections: DocumentSection[],
  index: number,
): number {
  const { min, max } = getSectionSlotInsertBounds(sections);
  return Math.max(min, Math.min(index, max));
}

export function canDeleteRow(
  document: { sections: DocumentSection[] },
  rowId: string,
): boolean {
  const located = locateRow(document.sections, rowId);
  if (!located) {
    return false;
  }
  if (located.location.parent === 'group') {
    return true;
  }
  return document.sections.length > 1;
}

export function canMoveSection(
  document: { sections: DocumentSection[] },
  sectionId: string,
  direction: -1 | 1,
): boolean {
  const index = document.sections.findIndex(
    section => section.id === sectionId,
  );
  if (index < 0) {
    return false;
  }
  const nextIndex = index + direction;
  return nextIndex >= 0 && nextIndex < document.sections.length;
}

export function canMoveRow(
  document: { sections: DocumentSection[] },
  rowId: string,
  direction: -1 | 1,
): boolean {
  const located = locateRow(document.sections, rowId);
  if (!located) {
    return false;
  }
  if (located.location.parent === 'group') {
    const group = findGroup(document.sections, located.location.groupId);
    if (!group) {
      return false;
    }
    const nextIndex = located.location.rowIndex + direction;
    return nextIndex >= 0 && nextIndex < group.rows.length;
  }
  return canMoveSection(document, rowId, direction);
}

export function canReorderRow(
  document: { sections: DocumentSection[] },
  rowId: string,
): boolean {
  void document;
  void rowId;
  return true;
}
